import React from "react";
import { useParams } from "react-router-dom";
import { Box, Center, Spinner, Alert, AlertIcon, AlertTitle, AlertDescription } from "@chakra-ui/react";
import { useFindOne } from "@gadgetinc/react";
import { api } from "../api";
import ScenarioDisplay from "../components/ScenarioDisplay";
import { buildScenarioDisplayProps } from "../lib/scenarioDisplayConfig";
import { buildGeneratedScenarioName } from "../lib/scenarioNaming";


// Set body class synchronously so CSS rules are active before first paint and before window.print() fires
document.body.className = 'simple-print';

export default function ScenarioPrintPage() {
  const { id } = useParams();

  const [{ data: scenario, fetching, error }] = useFindOne(api.clinicalScenario, id, {
    select: {
      id: true,
      title: true,
      levelOfCare: true,
      majorSystemInvolved: true,
      scenarioOutput: true,
      ageRange: true,
      ageBracket: true,
      difficulty: true,
      mechanismNatureOfInjury: true,
      createdAt: true,
      institution: true,
      isPublic: true,
    },
  });

  React.useEffect(() => {
    if (!scenario) return;
    const name = buildGeneratedScenarioName(scenario, scenario?.title) || "Clinical Scenario";
    document.title = name;
  }, [scenario]);

  React.useEffect(() => {
    if (!scenario) return;
    let cancelled = false;

    const onAfterPrint = () => {
      window.close();
    };
    window.addEventListener("afterprint", onAfterPrint);

    const waitForStableLayoutAndPrint = async () => {
      try {
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }
      } catch (e) {
        // ignore font readiness errors
      }

      const node = document.querySelector(".scenario-content-for-pdf");
      if (!node) return;

      let stableCount = 0;
      let previousHeight = 0;
      let checks = 0;
      while (!cancelled && checks < 40 && stableCount < 4) {
        await new Promise((resolve) => setTimeout(resolve, 120));
        const currentHeight = Math.max(node.scrollHeight, node.clientHeight);
        if (Math.abs(currentHeight - previousHeight) < 2 && currentHeight > 0) {
          stableCount += 1;
        } else {
          stableCount = 0;
          previousHeight = currentHeight;
        }
        checks += 1;
      }

      if (cancelled) return;

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (cancelled) return;

      window.focus();
      window.print();
    };

    waitForStableLayoutAndPrint();

    return () => {
      cancelled = true;
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, [scenario]);

  if (fetching) {
    return (
      <Center minH="100vh" bg="white">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Center>
    );
  }

  if (error || !scenario) {
    return (
      <Box p={6} bg="white" minH="100vh">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading scenario</AlertTitle>
          <AlertDescription>{error?.message || "Scenario not found"}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <ScenarioDisplay
        {...buildScenarioDisplayProps({
          scenarioOutput: scenario.scenarioOutput,
          scenarioRecord: scenario,
          allowPrintControls: false,
          initialPrintMode: "simple-print",
        })}
        dedicatedPrintMode={true}
      />
    </div>
  );
}